/**
 * Mark-to-market for a basket, and for each leg in it — ROADMAP 6.7.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A PARTIALLY MARKED BASKET IS ITS OWN ANSWER
 *
 * `unrealisedPnl` (6.4) returns null rather than 0 when there is no mark,
 * because zero is a real P&L — a position exactly at its entry — and an
 * unpriced position is not that. Summing a basket is where that care is
 * normally thrown away: four legs come back as three numbers and a null, the
 * null adds as zero, and the total reads as a complete valuation of a basket
 * one leg of which nobody can price.
 *
 * It is not a rare case. A far-OTM wing of an Iron Condor can go minutes
 * without a trade, and a strike subscribed seconds ago has no tick yet. The
 * leg carrying the most risk is often the one least likely to be marked.
 *
 * So the total is a TAGGED UNION, not a number:
 *
 *     COMPLETE  every open leg is marked; the value is the valuation
 *     PARTIAL   some are; the value covers only those, and says how many
 *     UNMARKED  none are; there is no value to read
 *
 * A caller cannot reach `value` on UNMARKED, and cannot read a PARTIAL as a
 * COMPLETE without branching on `kind` first. That is the point: the type
 * makes the UI say "3 of 4 marked" rather than quietly understating risk.
 *
 * REALISED NEEDS NO MARK. It comes from fills, so it is always a complete
 * number — reported separately for that reason, and kept GROSS per 6.4.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { netPnl, unrealisedPnl } from './pnl.js';
/** One leg's P&L. The only place a leg's own marks are interpreted. */
export function legMtm(leg) {
    const open = leg.netQty !== 0;
    const charges = Number.isFinite(leg.chargesTotal) ? Math.max(0, leg.chargesTotal) : 0;
    const realised = Number.isFinite(leg.realisedPnl) ? leg.realisedPnl : 0;
    // A flat leg has nothing left to mark, so its unrealised is 0 and known.
    const unrealised = open
        ? unrealisedPnl({ qty: leg.netQty, entryPrice: leg.avgPrice, ltp: leg.ltp })
        : 0;
    if (unrealised === null) {
        return { unrealised: null, realised, charges, gross: null, net: null, marked: false, open };
    }
    const gross = round2(realised + unrealised);
    return {
        unrealised,
        realised,
        charges,
        gross,
        net: netPnl(gross, charges),
        marked: true,
        open,
    };
}
/**
 * Combine legs into one basket.
 *
 * An EMPTY basket is COMPLETE at zero, not UNMARKED. "Nothing to value" and
 * "cannot value what is there" are different answers, and a strategy with no
 * open legs should read as flat rather than as a failure to price it.
 */
export function basketMtm(legs) {
    const computed = legs.map(legMtm);
    let realised = 0;
    let charges = 0;
    let markedUnrealised = 0;
    let marked = 0;
    let unmarked = 0;
    let openLegs = 0;
    for (const l of computed) {
        realised += l.realised;
        charges += l.charges;
        if (l.open)
            openLegs += 1;
        if (l.unrealised === null) {
            unmarked += 1;
            continue;
        }
        markedUnrealised += l.unrealised;
        marked += 1;
    }
    realised = round2(realised);
    charges = round2(charges);
    markedUnrealised = round2(markedUnrealised);
    const unrealised = totalOf(markedUnrealised, marked, unmarked);
    /*
     * Net carries the unrealised's completeness. The subtraction itself goes
     * through `netPnl` so this module does not become the second place charges
     * are subtracted — the failure 6.4's header describes.
     */
    const net = unrealised.kind === 'UNMARKED'
        ? unrealised
        : totalOf(netPnl(realised + unrealised.value, charges), marked, unmarked);
    return {
        legs: computed,
        realised,
        charges,
        unrealised,
        net,
        openLegs,
        closedLegs: computed.length - openLegs,
        unmarkedLegs: unmarked,
    };
}
function totalOf(value, marked, unmarked) {
    if (unmarked === 0)
        return { kind: 'COMPLETE', value };
    if (marked === 0)
        return { kind: 'UNMARKED', unmarked };
    return { kind: 'PARTIAL', value, marked, unmarked };
}
/**
 * The value to DISPLAY, and whether it is the whole story.
 *
 * For the one case a UI legitimately needs a number and a caveat rather than
 * three branches. Returns null for UNMARKED so there is still no way to print
 * a zero that means "unknown".
 */
export const totalValue = (t) => t.kind === 'UNMARKED' ? null : t.value;
/** Rupees to the paisa. Same rounding as `pnl.ts`. */
const round2 = (v) => Math.round(v * 100) / 100;
//# sourceMappingURL=basketMtm.js.map